import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Dice5 } from "lucide-react";
import { formatScore, scoreLabel } from "@/lib/scoreFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveScoreboard() {
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/tournaments/${tid}/scoreboard`);
        if (!cancelled) setData(r.data);
      } catch {}
    };
    load();
    const iv = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [tid]);

  useEffect(() => {
    const iv = setInterval(() => setTick(x => x + 1), 500);
    return () => clearInterval(iv);
  }, []);

  if (!data) return <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center">Loading…</div>;

  const activeRound = data.rounds.find(r => r.status === "active");
  let remainingSec = null;
  let isPaused = false;
  if (activeRound?.started_at) {
    const start = new Date(activeRound.started_at).getTime();
    const total = data.timer_minutes * 60 * 1000;
    const pausedMs = activeRound.paused_ms || 0;
    const nowRef = activeRound.paused_at ? new Date(activeRound.paused_at).getTime() : Date.now();
    isPaused = !!activeRound.paused_at;
    remainingSec = Math.max(0, Math.floor((start + total + pausedMs - nowRef) / 1000));
  }
  const fmt = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const nameOf = (uid) => data.participants.find(p => p.user_id === uid)?.name || uid;

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="glass border-b border-white/5 sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
              <Dice5 className="w-4 h-4 text-slate-950" />
            </div>
            <div>
              <div className="font-mono-data text-xs text-amber-400 tracking-widest">LIVE SCOREBOARD</div>
              <div className="text-slate-100 text-sm">{data.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-emerald-400 font-mono-data tracking-widest uppercase">LIVE</span>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-8 py-10">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Timer */}
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-slate-900/40 p-10">
            <div className="text-xs text-slate-500 font-mono-data tracking-widest">
              {activeRound ? `ROUND ${activeRound.round_number} · ${isPaused ? "PAUSED" : "IN PROGRESS"}` : `WAITING FOR NEXT ROUND`}
            </div>
            <div className={`mt-3 font-mono-data text-[10rem] leading-none ${remainingSec === 0 ? 'text-rose-400' : isPaused ? 'text-sky-200' : 'text-slate-50'}`}>
              {activeRound ? fmt(remainingSec) : "--:--"}
            </div>
            <div className="text-slate-500 mt-4 font-mono-data text-sm">
              {activeRound ? `${data.timer_minutes}min · Round ${data.current_round} of ${data.num_rounds}` : `${data.rounds.filter(r => r.status === 'locked').length} rounds locked`}
            </div>
          </div>

          {/* Top 3 */}
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8">
            <div className="text-xs text-amber-400 font-mono-data tracking-widest">CURRENT LEADERS</div>
            <div className="mt-4 space-y-4">
              {data.standings.slice(0, 3).map((s, i) => (
                <div key={s.user_id} className="flex items-baseline gap-4">
                  <div className={`font-mono-data text-4xl ${i === 0 ? 'text-amber-400' : 'text-slate-500'}`}>{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-slate-100 text-lg">{s.name}</div>
                    <div className="font-mono-data text-xs text-slate-500">Best round · {formatScore(s.best_round, data.tournament_type, { digits: 1 })}</div>
                  </div>
                  <div className="font-mono-data text-2xl text-slate-100">{formatScore(s.total_score, data.tournament_type, { digits: 1 })}</div>
                </div>
              ))}
              {data.standings.length === 0 && <div className="text-slate-500 text-sm">Scores appear here once round 1 locks.</div>}
            </div>
          </div>
        </div>

        {/* Full standings */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-slate-900/40 overflow-hidden">
          <div className="grid grid-cols-12 px-8 py-4 text-xs font-mono-data tracking-widest text-slate-500 border-b border-white/5">
            <div className="col-span-1">RANK</div>
            <div className="col-span-5">PLAYER</div>
            <div className="col-span-2 text-right">{scoreLabel(data.tournament_type)}</div>
            <div className="col-span-2 text-right">BEST</div>
            <div className="col-span-2 text-right">ROUNDS</div>
          </div>
          {data.standings.map((s, i) => (
            <div key={s.user_id} className={`grid grid-cols-12 px-8 py-4 items-center border-b border-white/5 ${i === 0 ? 'bg-amber-500/5' : ''}`}>
              <div className={`col-span-1 font-mono-data text-2xl ${i < 3 ? 'text-amber-400' : 'text-slate-500'}`}>{i + 1}</div>
              <div className="col-span-5 text-slate-100 text-lg">{s.name}</div>
              <div className="col-span-2 text-right font-mono-data text-2xl text-slate-100">{formatScore(s.total_score, data.tournament_type)}</div>
              <div className="col-span-2 text-right font-mono-data text-slate-400">{formatScore(s.best_round, data.tournament_type)}</div>
              <div className="col-span-2 text-right font-mono-data text-slate-500">{s.rounds_played}</div>
            </div>
          ))}
        </div>

        {/* Tables for active round */}
        {activeRound && activeRound.tables.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl text-slate-50">Table seating</h2>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeRound.tables.map(tbl => (
                <div key={tbl.table_number} className="rounded-xl border border-white/10 bg-slate-900/60 p-6">
                  <div className="text-xs text-emerald-400 font-mono-data tracking-widest">TABLE {tbl.table_number}</div>
                  <ul className="mt-3 space-y-2">
                    {tbl.player_ids.map(uid => (
                      <li key={uid} className="text-slate-200 border-b border-white/5 pb-2 last:border-0">{nameOf(uid)}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {activeRound.byes.length > 0 && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6">
                  <div className="text-xs text-rose-400 font-mono-data tracking-widest">BYE — SIT OUT</div>
                  <ul className="mt-3 space-y-2">
                    {activeRound.byes.map(uid => (
                      <li key={uid} className="text-rose-200">{nameOf(uid)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
